/**
 * Applying an imported CSV of target column names to the rows of the column-mappings modal.
 *
 * The problem it exists for: repointing an entity at a different source (an external table to its
 * `_psd` twin, say) leaves every column mapping dangling, because mappings key on
 * `source_entity_column_id` and the new entity's ids are different. Every target name then has to
 * be typed again, even where the column names are identical. This lets a configurator bring that
 * list in from a file instead.
 *
 * Matching is on SOURCE COLUMN NAME, case-insensitively. Names are what survive a repoint; ids are
 * exactly what does not.
 *
 * Nothing here writes: it returns the new rows plus a report, and the caller sets state. The modal
 * marks changed rows pending on its own, and the configurator presses Save.
 */

/** The subset of a mapping row this cares about. Keeps the helper testable without the modal. */
export type TargetNameImportRow = {
  source_entity_column_name?: string | null;
  target_entity_rdv_column_name?: string | null;
  is_inherited?: boolean | null;
  is_locked?: boolean | null;
};

export type TargetNameImportOutcome<Row> = {
  rows: Row[];
  /** Source column names that matched a row and changed its target name. */
  applied: string[];
  /** CSV rows naming a source column this entity does not have. */
  unmatched: string[];
  /** Matched, but the row is inherited or locked, so its name is not the modeller's to set. */
  skippedLocked: string[];
  /** Matched, but the target name already said exactly this. */
  unchanged: string[];
  /** Same source column named more than once in the file; later rows are ignored. */
  duplicated: string[];
};

const normalize = (value: string | null | undefined) => (value ?? "").trim().toLowerCase();

/**
 * Build the source-column -> target-name pairs from parsed CSV rows.
 *
 * Accepts a header in any column order, recognising `source_entity_column_name` / `source_column` /
 * `source` for the left side and `target_entity_rdv_column_name` / `target_column` / `target` for
 * the right. With no recognisable header the first two columns are used positionally, which is what
 * a two-column file hand-made in a spreadsheet looks like.
 */
export const readTargetNamePairs = (
  rows: string[][],
  header: Map<string, number> | null
): Array<{ source: string; target: string }> => {
  const pick = (names: string[], fallback: number): number => {
    if (!header) {
      return fallback;
    }
    for (const name of names) {
      const index = header.get(name);
      if (index !== undefined) {
        return index;
      }
    }
    return -1;
  };

  const sourceAt = pick(["source_entity_column_name", "source_column", "source"], 0);
  const targetAt = pick(
    ["target_entity_rdv_column_name", "target_column", "target"],
    1
  );
  const body = header ? rows.slice(1) : rows;

  return body
    .map((cells) => ({
      source: (sourceAt >= 0 ? cells[sourceAt] ?? "" : "").trim(),
      target: (targetAt >= 0 ? cells[targetAt] ?? "" : "").trim()
    }))
    .filter((pair) => pair.source.length > 0);
};

/**
 * Apply the pairs to the rows, returning new rows and a report of what happened.
 *
 * A pair whose target is blank is treated as "no opinion" and leaves the row alone, so a file
 * exported with some names still empty does not wipe names already set.
 */
export const applyTargetNames = <Row extends TargetNameImportRow>(
  rows: Row[],
  pairs: Array<{ source: string; target: string }>
): TargetNameImportOutcome<Row> => {
  const wanted = new Map<string, string>();
  const duplicated: string[] = [];
  pairs.forEach((pair) => {
    const key = normalize(pair.source);
    if (!key || !pair.target) {
      return;
    }
    if (wanted.has(key)) {
      duplicated.push(pair.source);
      return;
    }
    wanted.set(key, pair.target);
  });

  const applied: string[] = [];
  const skippedLocked: string[] = [];
  const unchanged: string[] = [];
  const matched = new Set<string>();

  const next = rows.map((row) => {
    const key = normalize(row.source_entity_column_name);
    if (!key || !wanted.has(key)) {
      return row;
    }
    matched.add(key);
    const target = wanted.get(key) as string;

    if (row.is_inherited || row.is_locked) {
      skippedLocked.push(row.source_entity_column_name as string);
      return row;
    }
    if ((row.target_entity_rdv_column_name ?? "") === target) {
      unchanged.push(row.source_entity_column_name as string);
      return row;
    }
    applied.push(row.source_entity_column_name as string);
    return { ...row, target_entity_rdv_column_name: target };
  });

  const unmatched = pairs
    .filter((pair) => pair.target && !matched.has(normalize(pair.source)))
    .map((pair) => pair.source)
    .filter((name, index, all) => all.indexOf(name) === index);

  return { rows: next, applied, unmatched, skippedLocked, unchanged, duplicated };
};

/** One line a human can read, for the modal's status area. */
export const describeTargetNameImport = (
  outcome: TargetNameImportOutcome<unknown>
): string => {
  const parts = [
    `${outcome.applied.length} column${outcome.applied.length === 1 ? "" : "s"} named`
  ];
  if (outcome.unchanged.length) {
    parts.push(`${outcome.unchanged.length} already matched`);
  }
  if (outcome.skippedLocked.length) {
    parts.push(`${outcome.skippedLocked.length} skipped as inherited`);
  }
  if (outcome.unmatched.length) {
    parts.push(`${outcome.unmatched.length} not on this source`);
  }
  if (outcome.duplicated.length) {
    parts.push(`${outcome.duplicated.length} duplicate row${outcome.duplicated.length === 1 ? "" : "s"} ignored`);
  }
  return parts.join(", ") + ".";
};
